import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, MapPin, DollarSign, Building2, TrendingUp,
  AlertCircle, Share2, FileText, ChevronRight, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import ResponsibilityNotice from '../components/ResponsibilityNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const YEAR_ELEITORAL = 2026;

const brl = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
    : '—';

const formatCnpj = (raw) => {
  const d = String(raw || '').replace(/\D/g, '').slice(0, 14);
  if (d.length !== 14) return raw || '—';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const AREA_META = {
  saude:          { label: 'Saúde',              emoji: '🏥' },
  educacao:       { label: 'Educação',           emoji: '🎓' },
  infraestrutura: { label: 'Infraestrutura',     emoji: '🏗️' },
  esporte:        { label: 'Esporte',            emoji: '⚽' },
  meio_ambiente:  { label: 'Meio Ambiente',      emoji: '🌱' },
  cultura:        { label: 'Cultura',            emoji: '🎭' },
  assistencia:    { label: 'Assistência Social', emoji: '👥' },
  agropecuaria:   { label: 'Agropecuária',       emoji: '🚜' },
  seguranca:      { label: 'Segurança',          emoji: '🛡️' },
  outros:         { label: 'Outros',             emoji: '📌' },
};

const EmendasMunicipio = () => {
  const { cod_ibge } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await axios.get(`${API}/public/emendas/municipio/${cod_ibge}`, { timeout: 20000 });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Não foi possível carregar as emendas deste município.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cod_ibge]);

  const share = async () => {
    const url = window.location.href;
    const nome = data?.municipio?.nome || 'Município';
    const uf = data?.municipio?.uf || '';
    const text = `Emendas parlamentares recebidas por ${nome}/${uf}. Dados oficiais no Tô de Olho.`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Tô de Olho', text, url }); return; } catch (_) { /* fallback */ }
    }
    try {
      await navigator.clipboard?.writeText(`${text}\n${url}`);
      toast.success('Link copiado.');
    } catch (_) {
      toast.info(`Copie manualmente. ${url}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-10 text-sm text-[#475569]" data-testid="emendas-muni-loading">
        Carregando emendas do município...
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-10" data-testid="emendas-muni-error">
        <Card className="p-6 border border-[#FCA5A5] bg-[#FEE2E2]">
          <div className="flex items-start gap-2 text-[#991B1B]">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Município sem emendas registradas.</p>
              <p className="text-sm mt-1">{error}</p>
              <Button variant="outline" onClick={() => navigate('/emendas')} className="mt-3" data-testid="emendas-muni-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar para busca
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const muni = data.municipio || {};
  const kpis = data.kpis || {};
  const dist = data.distribuicao_area || [];
  const topInst = data.top_instituicoes || [];
  const porAno = data.por_ano || [];
  const amostra = data.emendas_amostra || [];
  const totalReferencia = kpis.total_pago || kpis.total_empenhado || 0;

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="emendas-muni-page">
      <button
        type="button"
        onClick={() => navigate('/emendas')}
        className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#006030]"
        data-testid="emendas-muni-back"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar para busca
      </button>

      {/* Header identidade */}
      <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-muni-identity">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="w-16 h-16 rounded-full bg-[#E8F5E9] flex items-center justify-center shrink-0">
              <MapPin className="w-8 h-8 text-[#006030]" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg md:text-2xl font-black text-[#0F1C2E] leading-tight" data-testid="emendas-muni-nome">
                {muni.nome} <span className="text-[#94A3B8]">/ {muni.uf}</span>
              </h1>
              <p className="text-xs text-[#475569] mt-1">
                Emendas parlamentares recebidas · Cód. IBGE {muni.cod_ibge}
              </p>
              <p className="text-[10px] text-[#94A3B8] mt-1 uppercase tracking-wider">
                Fonte. Tesouro Transparente (dados oficiais)
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={share}
            className="border-[#006030] text-[#006030] hover:bg-[#E8F5E9] w-full sm:w-auto"
            data-testid="emendas-muni-share-btn"
          >
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> Compartilhar
          </Button>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="emendas-muni-kpis">
        <Card className="p-4 border border-[#E2E8F0]" data-testid="muni-kpi-total">
          <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
            <DollarSign className="w-3 h-3" /> Total recebido
          </div>
          <p className="text-lg md:text-xl font-black text-[#006030] mt-1">{brl(totalReferencia)}</p>
        </Card>
        <Card className="p-4 border border-[#E2E8F0]" data-testid="muni-kpi-pagamentos">
          <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
            <FileText className="w-3 h-3" /> Pagamentos
          </div>
          <p className="text-lg md:text-xl font-black text-[#0F1C2E] mt-1">{kpis.num_emendas || 0}</p>
        </Card>
        <Card className="p-4 border border-[#E2E8F0]" data-testid="muni-kpi-instituicoes">
          <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
            <Building2 className="w-3 h-3" /> Instituições
          </div>
          <p className="text-lg md:text-xl font-black text-[#0F1C2E] mt-1">{kpis.num_instituicoes || 0}</p>
        </Card>
        <Card className="p-4 border border-[#E2E8F0]" data-testid="muni-kpi-executado">
          <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
            <TrendingUp className="w-3 h-3" /> Executado
          </div>
          <p className="text-lg md:text-xl font-black text-[#006030] mt-1">{kpis.pct_executado || 0}%</p>
        </Card>
      </div>

      {/* Distribuição por área */}
      {dist.length > 0 && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-muni-distribuicao">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Distribuição por área</h2>
          <div className="space-y-2.5">
            {dist.map((d) => {
              const meta = AREA_META[d.area] || AREA_META.outros;
              const pct = totalReferencia ? Math.max(4, Math.round((d.valor / totalReferencia) * 100)) : 0;
              return (
                <div key={d.area} data-testid={`emendas-muni-area-${d.area}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-semibold text-[#0F1C2E]">{meta.emoji} {meta.label}</span>
                    <span className="font-bold text-[#006030]">{brl(d.valor)}</span>
                  </div>
                  <div className="h-2.5 bg-[#F1F5F9] rounded overflow-hidden">
                    <div className="h-full bg-[#006030]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Evolução por ano */}
      {porAno.length > 0 && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-muni-timeline">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Evolução por ano</h2>
          <div className="space-y-2">
            {porAno.map((p) => {
              const maxVal = Math.max(...porAno.map((x) => x.empenhado));
              const pctEmp = maxVal ? Math.max(6, Math.round((p.empenhado / maxVal) * 100)) : 0;
              const pctPago = maxVal ? Math.round((p.pago / maxVal) * 100) : 0;
              const isEleitoral = p.ano === YEAR_ELEITORAL;
              return (
                <div key={p.ano} data-testid={`emendas-muni-ano-${p.ano}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-[#0F1C2E] inline-flex items-center gap-1">
                      {p.ano}
                      {isEleitoral && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-mono uppercase" data-testid={`muni-badge-eleitoral-${p.ano}`}>
                          Eleitoral
                        </span>
                      )}
                    </span>
                    <span className="text-[#475569]">Empenhado {brl(p.empenhado)} · Pago {brl(p.pago)}</span>
                  </div>
                  <div className="relative h-3 bg-[#F1F5F9] rounded overflow-hidden">
                    <div className={`absolute top-0 left-0 h-full ${isEleitoral ? 'bg-amber-200' : 'bg-[#E2E8F0]'}`} style={{ width: `${pctEmp}%` }} />
                    <div className={`absolute top-0 left-0 h-full ${isEleitoral ? 'bg-amber-500' : 'bg-[#006030]'}`} style={{ width: `${pctPago}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Top instituições beneficiadas (clickable) */}
      {topInst.length > 0 && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-muni-top-inst">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
            Instituições que receberam
          </h2>
          <div className="space-y-2">
            {topInst.map((i, idx) => {
              const hasCnpj = i.cnpj && String(i.cnpj).replace(/\D/g, '').length === 14;
              const inner = (
                <div className="flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0">
                  <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-[#006030]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0F1C2E] truncate">{i.nome || 'Sem nome'}</p>
                    <p className="text-[11px] text-[#94A3B8] truncate">
                      {hasCnpj ? formatCnpj(i.cnpj) : 'CNPJ não informado'} · {i.tipo || 'Favorecido'} · {i.num_emendas || 0} emenda(s)
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-[#006030]">{brl(i.valor)}</p>
                  </div>
                  {hasCnpj && <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0" />}
                </div>
              );
              return hasCnpj ? (
                <Link
                  key={i.cnpj || idx}
                  to={`/empresa/${i.cnpj}`}
                  className="block hover:bg-[#F9FAFB] -mx-2 px-2 rounded"
                  data-testid={`emendas-muni-inst-${idx}`}
                >
                  {inner}
                </Link>
              ) : (
                <div key={idx} data-testid={`emendas-muni-inst-${idx}`}>
                  {inner}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Amostra de emendas — Linha do dinheiro (pagamentos individuais) */}
      {amostra.length > 0 && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="emendas-muni-amostra">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-1">
            Últimos pagamentos rastreados
          </h2>
          <p className="text-[11px] text-[#94A3B8] mb-3">
            Cada linha representa uma ordem bancária (OB) executada. Fonte oficial: Tesouro Transparente.
          </p>
          <div className="space-y-2">
            {amostra.slice(0, 50).map((em, idx) => {
              const fav = em.favorecido || {};
              const hasCnpj = fav.cnpj && String(fav.cnpj).replace(/\D/g, '').length === 14;
              const areaKey = em.area || 'outros';
              const areaMeta = AREA_META[areaKey] || AREA_META.outros;
              return (
                <div
                  key={em.ob || idx}
                  className="flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0"
                  data-testid={`emendas-muni-amostra-${idx}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#0F1C2E] truncate">
                      {hasCnpj ? (
                        <Link
                          to={`/empresa/${fav.cnpj}`}
                          className="hover:text-[#006030] underline-offset-2 hover:underline"
                          data-testid={`emendas-muni-amostra-fav-${idx}`}
                        >
                          {fav.nome || 'Sem nome'}
                        </Link>
                      ) : (
                        fav.nome || 'Sem nome'
                      )}
                    </p>
                    <p className="text-[11px] text-[#94A3B8]">
                      <span className="inline-flex items-center gap-1 mr-2">
                        <Calendar className="w-3 h-3" /> {em.ano || '—'}
                      </span>
                      {areaMeta.emoji} {areaMeta.label} · {em.tipo_emenda || 'Emenda'}
                      {em.orgao && ` · ${em.orgao}`}
                    </p>
                  </div>
                  <div className="text-sm font-bold text-[#006030] shrink-0">{brl(em.valor_pago || em.valor_empenhado)}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <ResponsibilityNotice
        source="Tesouro Transparente"
        sourceUrl="https://www.tesourotransparente.gov.br/ckan/dataset/emendas-parlamentares-individuais-e-de-bancada"
      />
    </div>
  );
};

export default EmendasMunicipio;
